import type { GuideHint } from '../game/guide';
import type { ServerStatus } from '../net/serverClient';
import type { GameSetup } from './gameSetup';
import type { SettingsStore } from './settings';
import type { RadioPlayer } from '../audio/radio';
import type { MenuPanel } from './menu/HomeScreen';

/** Application state that lives outside the game rules but shows up on the table's tablets. */
export interface ShellState {
  /** The setup the menus are drafting: what New Game, Restart and the presets start from. */
  setup: GameSetup;
  /**
   * Who plays whom in the game on the table, and each AI's levels: what the game tablet shows and
   * Play again restarts. Not the draft: a restored, cloud-loaded or online game has its own.
   */
  table: GameSetup;
  /** Whether a game is actually on the table, as opposed to the idle board behind the menu. */
  playing: boolean;
  /** Heavy smoke over the board; a setting, shown on the board and set in Settings. */
  fog: boolean;
  server: ServerStatus;
  xrSupported: boolean | null;
  inXr: boolean;
  /** null is the live board; menus keep their own page when entering/leaving VR. */
  page?: MenuPanel | null;
  version: string;
  /** The guided first game's lesson on show, written on the sign beyond the table. */
  guide: GuideHint | null;
}

/**
 * Bridges React (which owns the menus, the settings and the server check) and the Babylon scene
 * (which draws the tablets): React publishes, the scene subscribes and calls back.
 */
export class ShellStore {
  screenElement: HTMLElement | null = null;
  settings: SettingsStore | null = null;
  radio: RadioPlayer | null = null;
  private state: ShellState;
  private readonly listeners = new Set<() => void>();
  /** Set by the app: start a new game with the given setup. */
  onNewGame: ((setup: GameSetup) => void) | null = null;
  /** Set by the app: open the home screen (desktop; the tablets stay the interface in VR). */
  onMenu: (() => void) | null = null;
  /** Set by the app: throw the running game away and come back to the main menu. */
  onQuitGame: (() => void) | null = null;
  /** Set by the app: the player tapped Got it under the guide's lesson. */
  onGuideDismiss: (() => void) | null = null;

  constructor(initial: ShellState) {
    this.state = initial;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getSnapshot(): ShellState {
    return this.state;
  }

  set(patch: Partial<ShellState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }
}
